import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { PlacedOrder, RebalancePlanResponse, SettingsResponse, TradesResponse, TradeDetail } from '../types/api';
import { RefreshCw, Play, AlertCircle, FileText, ArrowRightLeft, Info, Calendar, TrendingUp, TrendingDown, BarChart3, ClipboardList } from 'lucide-react';
import { formatDateTimeEastern } from '../lib/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { CollapsibleCard } from '../components/CollapsibleCard';

const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

type TabType = 'orders' | 'trades';

export function Orders() {
  const { tradingMode } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('orders');
  const [orders, setOrders] = useState<PlacedOrder[]>([]);
  const [tradesData, setTradesData] = useState<TradesResponse | null>(null);
  const [plan, setPlan] = useState<RebalancePlanResponse | null>(null);
  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(getTodayDate());

  const isAutoExecuteEnabled = tradingMode === 'LIVE'
    ? settings?.live_auto_execute
    : settings?.paper_auto_execute;

  const loadData = async (dateFilter?: string) => {
    setLoading(true);
    try {
      const ordersData = await api.getOrders(dateFilter || selectedDate);
      setOrders(ordersData.orders);
    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTrades = async (dateFilter?: string) => {
    setTradesLoading(true);
    try {
      const data = await api.getTrades(dateFilter || selectedDate);
      setTradesData(data);
    } catch (err) {
      console.error('Error loading trades:', err);
    } finally {
      setTradesLoading(false);
    }
  };

  // Fast load — returns existing plan for today without triggering eval
  const loadTodayPlan = async () => {
    try {
      const planData = await api.getTodayPlan();
      setPlan(planData);
    } catch (err) {
      console.error('Error loading today plan:', err);
    }
  };

  // Explicit plan generation — triggers full target weights eval
  const loadPlan = async () => {
    setGeneratingPlan(true);
    setError(null);
    try {
      const planData = await api.generatePlan();
      setPlan(planData);
    } catch (err) {
      console.error('Error generating plan:', err);
      setError('Failed to generate plan. Please try again.');
    } finally {
      setGeneratingPlan(false);
    }
  };

  const loadSettings = async () => {
    try {
      const settingsData = await api.getSettings();
      setSettings(settingsData);
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  useEffect(() => {
    // Run all three in parallel — getTodayPlan is a fast DB lookup, never triggers eval
    loadData(selectedDate);
    loadTodayPlan();
    loadSettings();
  }, [selectedDate]);

  useEffect(() => {
    if (activeTab === 'trades') {
      loadTrades(selectedDate);
    }
  }, [activeTab, selectedDate]);

  const handleExecute = async () => {
    if (!plan?.run_id) return;
    
    setExecuting(true);
    setError(null);
    try {
      await api.executePlan(plan.run_id);
      await loadData();
      await loadTodayPlan();
      if (activeTab === 'trades') {
        await loadTrades();
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to execute orders';
      setError(errorMessage);
    } finally {
      setExecuting(false);
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  const formatPnl = (value: number) => {
    const formatted = formatCurrency(Math.abs(value));
    return value >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  const formatPct = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="text-sm text-gray-600 font-medium">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Orders</h1>
          <p className="text-gray-600 mt-1 text-base">Manage rebalance plans, order execution, and trade analysis</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadPlan}
            disabled={generatingPlan || executing}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 text-base font-semibold rounded-lg bg-white border border-gray-300 text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 transition-all"
          >
            {generatingPlan
              ? <RefreshCw className="w-5 h-5 animate-spin" />
              : <ClipboardList className="w-5 h-5" />}
            {generatingPlan ? 'Generating...' : 'Generate Plan'}
          </button>
          {!isAutoExecuteEnabled && (
            <button
              onClick={handleExecute}
              disabled={executing || generatingPlan || !plan?.planned_orders.length}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 text-base font-semibold rounded-lg bg-emerald-700 text-white shadow-md hover:bg-emerald-800 disabled:opacity-50 transition-all"
            >
              <Play className="w-5 h-5" />
              {executing ? 'Executing...' : 'Execute Orders'}
            </button>
          )}
        </div>
      </div>

      {isAutoExecuteEnabled && (
        <CollapsibleCard title="Auto Execute Info" icon={<Info className="w-5 h-5 text-amber-600" />}>
          <div className="px-6 py-4 text-base text-amber-900 bg-amber-50">
            Auto Execute is enabled. Orders will be executed automatically during scheduled rebalances.
          </div>
        </CollapsibleCard>
      )}

      {error && (
        <div className="flex items-center gap-3 px-5 py-4 rounded-lg text-base bg-red-50 border border-red-200 text-red-900  shadow-md">
          <AlertCircle className="w-6 h-6 flex-shrink-0" />
          <span className="font-semibold">{error}</span>
        </div>
      )}

      {plan && plan.planned_orders.length > 0 && (
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden ">
          <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Planned Orders</h2>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold bg-red-600 text-white shadow-md">{plan.sell_orders_count} sells</span>
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold bg-emerald-600 text-white shadow-md">{plan.buy_orders_count} buys</span>
            </div>
          </div>
          
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Symbol</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Side</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">Quantity</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">Est. Value</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">Shares Change</th>
                </tr>
              </thead>
              <tbody>
                {plan.planned_orders.map((order, idx) => (
                  <tr key={idx} className={`border-b border-gray-200 transition-all duration-200 hover:bg-gray-50 ${idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                    <td className="px-6 py-5 font-bold text-gray-900 text-base">{order.symbol}</td>
                    <td className="px-6 py-5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold shadow-sm ${order.side === 'BUY' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
                        {order.side}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-right text-base text-gray-700 font-bold">{order.quantity}</td>
                    <td className="px-6 py-5 text-right text-base text-gray-700 font-semibold">{formatCurrency(order.estimated_value)}</td>
                    <td className="px-6 py-5 text-right">
                      <span className="inline-flex items-center gap-1 text-base text-gray-600 font-medium">
                        {order.current_shares}
                        <ArrowRightLeft className="w-4 h-4 text-slate-400" />
                        {order.target_shares}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden divide-y divide-slate-100">
            {plan.planned_orders.map((order, idx) => (
              <div key={idx} className="p-5 space-y-3 hover:bg-gray-50 transition-all">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-gray-900 text-xl">{order.symbol}</span>
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold shadow-sm ${order.side === 'BUY' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
                    {order.side}
                  </span>
                </div>
                <div className="flex justify-between text-base text-gray-700 font-medium">
                  <span>Qty: {order.quantity}</span>
                  <span className="font-semibold">{formatCurrency(order.estimated_value)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-1 bg-gray-200 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                  activeTab === 'orders'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Order History
              </button>
              <button
                onClick={() => setActiveTab('trades')}
                className={`px-4 py-2 rounded-md text-sm font-semibold transition-all flex items-center gap-1.5 ${
                  activeTab === 'trades'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                Trades
              </button>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-500" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={getTodayDate()}
                className="px-4 py-2 border border-gray-300 rounded-lg text-base font-medium text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              {selectedDate !== getTodayDate() && (
                <button
                  onClick={() => setSelectedDate(getTodayDate())}
                  className="px-3 py-2 text-sm font-medium text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors"
                >
                  Today
                </button>
              )}
            </div>
          </div>
        </div>

        {activeTab === 'orders' ? (
          <OrdersTab
            orders={orders}
            selectedDate={selectedDate}
            formatCurrency={formatCurrency}
          />
        ) : (
          <TradesTab
            tradesData={tradesData}
            loading={tradesLoading}
            selectedDate={selectedDate}
            formatCurrency={formatCurrency}
            formatPnl={formatPnl}
            formatPct={formatPct}
          />
        )}
      </div>
    </div>
  );
}

function OrdersTab({ orders, selectedDate, formatCurrency }: {
  orders: PlacedOrder[];
  selectedDate: string;
  formatCurrency: (v: number) => string;
}) {
  if (orders.length === 0) {
    return (
      <div className="text-center py-16 text-gray-600">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
          <FileText className="w-10 h-10 text-slate-400" />
        </div>
        <p className="font-semibold text-gray-600 text-lg">
          {selectedDate === getTodayDate()
            ? 'No orders executed today'
            : `No orders found for ${new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
        </p>
        <p className="text-base text-slate-400 mt-1">
          {selectedDate === getTodayDate()
            ? 'Execute a plan to see order history'
            : 'Try selecting a different date'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Symbol</th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Side</th>
              <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">Quantity</th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">Fill Price</th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Time</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, idx) => (
              <tr key={order.id} className={`border-b border-gray-200 transition-all duration-200 hover:bg-gray-50 ${idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                <td className="px-6 py-5 font-bold text-gray-900 text-base">{order.symbol}</td>
                <td className="px-6 py-5">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold shadow-sm ${order.side === 'BUY' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
                    {order.side}
                  </span>
                </td>
                <td className="px-6 py-5 text-right text-base text-gray-700 font-bold">{order.quantity}</td>
                <td className="px-6 py-5">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold shadow-sm ${
                    order.status === 'FILLED' ? 'bg-emerald-600 text-white' :
                    order.status === 'REJECTED' ? 'bg-red-600 text-white' :
                    'bg-slate-200 text-gray-700'
                  }`}>
                    {order.status}
                  </span>
                </td>
                <td className="px-6 py-5 text-right text-base text-gray-700 font-semibold">
                  {order.filled_price ? formatCurrency(order.filled_price) : '--'}
                </td>
                <td className="px-6 py-5 text-base text-gray-600">
                  {order.submitted_at ? formatDateTimeEastern(order.submitted_at) : '--'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden divide-y divide-slate-100">
        {orders.map((order) => (
          <div key={order.id} className="p-5 space-y-3 hover:bg-gray-50 transition-all">
            <div className="flex justify-between items-center">
              <span className="font-bold text-gray-900 text-lg">{order.symbol}</span>
              <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold shadow-sm ${
                order.status === 'FILLED' ? 'bg-emerald-600 text-white' :
                order.status === 'REJECTED' ? 'bg-red-600 text-white' :
                'bg-slate-200 text-gray-700'
              }`}>
                {order.status}
              </span>
            </div>
            <div className="flex justify-between text-base text-gray-700 font-medium">
              <span>{order.side} {order.quantity}</span>
              <span className="font-semibold">{order.filled_price ? formatCurrency(order.filled_price) : '--'}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function TradesTab({ tradesData, loading, selectedDate, formatCurrency, formatPnl, formatPct }: {
  tradesData: TradesResponse | null;
  loading: boolean;
  selectedDate: string;
  formatCurrency: (v: number) => string;
  formatPnl: (v: number) => string;
  formatPct: (v: number) => string;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="text-sm text-gray-600 font-medium">Loading trades...</span>
        </div>
      </div>
    );
  }

  if (!tradesData || tradesData.trades.length === 0) {
    return (
      <div className="text-center py-16 text-gray-600">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
          <BarChart3 className="w-10 h-10 text-slate-400" />
        </div>
        <p className="font-semibold text-gray-600 text-lg">
          {selectedDate === getTodayDate()
            ? 'No trades executed today'
            : `No trades found for ${new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
        </p>
        <p className="text-base text-slate-400 mt-1">
          Filled orders with EOD price comparison will appear here
        </p>
      </div>
    );
  }

  const { trades, equity_before, daily_return_pct, daily_pnl } = tradesData;
  const totalBuys = trades.filter(t => t.side === 'BUY').length;
  const totalSells = trades.filter(t => t.side === 'SELL').length;

  return (
    <div>
      <CollapsibleCard title="Trade Summary">
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Trades</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5">
                {trades.length}
                <span className="text-sm font-medium text-gray-500 ml-1">
                  ({totalBuys}B / {totalSells}S)
                </span>
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Portfolio Value</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5">
                {equity_before ? formatCurrency(equity_before) : '--'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Daily P&L</p>
              <p className={`text-lg font-bold mt-0.5 ${
                daily_pnl === null || daily_pnl === undefined ? 'text-gray-400' :
                daily_pnl >= 0 ? 'text-emerald-700' : 'text-red-600'
              }`}>
                {daily_pnl !== null && daily_pnl !== undefined ? formatPnl(daily_pnl) : '--'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Daily Return</p>
              <div className={`flex items-center justify-center gap-1 mt-0.5 ${
                daily_return_pct === null || daily_return_pct === undefined ? 'text-gray-400' :
                daily_return_pct >= 0 ? 'text-emerald-700' : 'text-red-600'
              }`}>
                {daily_return_pct !== null && daily_return_pct !== undefined ? (
                  <>
                    {daily_return_pct >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                    <span className="text-lg font-bold">{formatPct(daily_return_pct)}</span>
                  </>
                ) : (
                  <span className="text-lg font-bold">--</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </CollapsibleCard>

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Symbol</th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Side</th>
              <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">Qty</th>
              <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">Fill Price</th>
              <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">EOD Price</th>
              <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">Trade Value</th>
              <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">P&L</th>
              <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">Return</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((trade, idx) => (
              <TradeRow key={trade.id} trade={trade} idx={idx} formatCurrency={formatCurrency} formatPnl={formatPnl} formatPct={formatPct} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden divide-y divide-slate-100">
        {trades.map((trade) => (
          <TradeCard key={trade.id} trade={trade} formatCurrency={formatCurrency} formatPnl={formatPnl} formatPct={formatPct} />
        ))}
      </div>
    </div>
  );
}

function TradeRow({ trade, idx, formatCurrency, formatPnl, formatPct }: {
  trade: TradeDetail;
  idx: number;
  formatCurrency: (v: number) => string;
  formatPnl: (v: number) => string;
  formatPct: (v: number) => string;
}) {
  return (
    <tr className={`border-b border-gray-200 transition-all duration-200 hover:bg-gray-50 ${idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
      <td className="px-6 py-5 font-bold text-gray-900 text-base">{trade.symbol}</td>
      <td className="px-6 py-5">
        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold shadow-sm ${trade.side === 'BUY' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
          {trade.side}
        </span>
      </td>
      <td className="px-6 py-5 text-right text-base text-gray-700 font-bold">{trade.quantity}</td>
      <td className="px-6 py-5 text-right text-base text-gray-700 font-semibold">{formatCurrency(trade.fill_price)}</td>
      <td className="px-6 py-5 text-right text-base text-gray-700 font-semibold">
        {trade.eod_price ? formatCurrency(trade.eod_price) : '--'}
      </td>
      <td className="px-6 py-5 text-right text-base text-gray-700 font-semibold">{formatCurrency(trade.trade_value)}</td>
      <td className={`px-6 py-5 text-right text-base font-bold ${
        trade.trade_pnl === null || trade.trade_pnl === undefined ? 'text-gray-400' :
        trade.trade_pnl >= 0 ? 'text-emerald-700' : 'text-red-600'
      }`}>
        {trade.trade_pnl !== null && trade.trade_pnl !== undefined ? formatPnl(trade.trade_pnl) : '--'}
      </td>
      <td className={`px-6 py-5 text-right text-base font-bold ${
        trade.trade_pnl_pct === null || trade.trade_pnl_pct === undefined ? 'text-gray-400' :
        trade.trade_pnl_pct >= 0 ? 'text-emerald-700' : 'text-red-600'
      }`}>
        {trade.trade_pnl_pct !== null && trade.trade_pnl_pct !== undefined ? formatPct(trade.trade_pnl_pct) : '--'}
      </td>
    </tr>
  );
}

function TradeCard({ trade, formatCurrency, formatPnl, formatPct }: {
  trade: TradeDetail;
  formatCurrency: (v: number) => string;
  formatPnl: (v: number) => string;
  formatPct: (v: number) => string;
}) {
  return (
    <div className="p-5 space-y-3 hover:bg-gray-50 transition-all">
      <div className="flex justify-between items-center">
        <span className="font-bold text-gray-900 text-lg">{trade.symbol}</span>
        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold shadow-sm ${trade.side === 'BUY' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
          {trade.side}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-gray-500">Qty:</span>
          <span className="font-bold text-gray-900 ml-1">{trade.quantity}</span>
        </div>
        <div className="text-right">
          <span className="text-gray-500">Value:</span>
          <span className="font-semibold text-gray-900 ml-1">{formatCurrency(trade.trade_value)}</span>
        </div>
        <div>
          <span className="text-gray-500">Fill:</span>
          <span className="font-semibold text-gray-900 ml-1">{formatCurrency(trade.fill_price)}</span>
        </div>
        <div className="text-right">
          <span className="text-gray-500">EOD:</span>
          <span className="font-semibold text-gray-900 ml-1">{trade.eod_price ? formatCurrency(trade.eod_price) : '--'}</span>
        </div>
      </div>
      {trade.trade_pnl !== null && trade.trade_pnl !== undefined && (
        <div className={`flex justify-between items-center pt-2 border-t border-gray-100 ${
          trade.trade_pnl >= 0 ? 'text-emerald-700' : 'text-red-600'
        }`}>
          <span className="font-bold">{formatPnl(trade.trade_pnl)}</span>
          <span className="font-bold">{trade.trade_pnl_pct !== null && trade.trade_pnl_pct !== undefined ? formatPct(trade.trade_pnl_pct) : ''}</span>
        </div>
      )}
    </div>
  );
}
